import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { CheckCircle, Calendar, Loader2, ExternalLink } from "lucide-react";
import { Button } from "../components/ui/button";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

export default function ConsultationSuccess() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = searchParams.get("session_id");
  
  const [status, setStatus] = useState("loading");
  const [calendlyUrl, setCalendlyUrl] = useState(null);
  const [booking, setBooking] = useState(null);

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }

    const checkStatus = async () => {
      try {
        const response = await axios.get(`${API}/api/consultation/status/${sessionId}`);
        
        if (response.data.payment_status === "paid") {
          setStatus("success");
          setCalendlyUrl(response.data.calendly_url);
          setBooking(response.data.booking);
        } else if (response.data.payment_status === "pending") {
          // Retry after 2 seconds
          setTimeout(checkStatus, 2000);
        } else {
          setStatus("error");
        }
      } catch (err) {
        console.error("Status check error:", err);
        setStatus("error");
      }
    };

    checkStatus();
  }, [sessionId]);

  const handleCalendlyClick = () => {
    if (calendlyUrl) {
      window.open(calendlyUrl, "_blank", "noopener,noreferrer");
    }
  };

  if (status === "loading") {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#F5F1EB] to-white flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-[#2F4F4F] animate-spin mx-auto mb-4" />
          <p className="text-[#5C5C5C]">Vérification du paiement...</p>
        </div>
      </div>
    );
  }

  if (status === "error") {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#F5F1EB] to-white flex items-center justify-center px-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-3xl">❌</span>
          </div>
          <h1 className="text-2xl font-bold text-[#2D2D2D] mb-2">
            Une erreur est survenue
          </h1>
          <p className="text-[#5C5C5C] mb-6">
            Le paiement n'a pas pu être vérifié. Veuillez réessayer ou nous contacter.
          </p>
          <Button
            onClick={() => navigate("/")}
            className="bg-[#2F4F4F] hover:bg-[#2F4F4F]/90"
          >
            Retour à l'accueil
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#F5F1EB] to-white flex items-center justify-center px-4">
      <div className="bg-white rounded-2xl shadow-lg overflow-hidden max-w-lg w-full">
        {/* Success Header */}
        <div className="bg-gradient-to-r from-[#2F4F4F] to-[#3B7A8C] p-8 text-center text-white">
          <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-12 h-12" />
          </div>
          <h1 className="text-2xl font-bold mb-2">Paiement confirmé !</h1>
          <p className="text-white/80">Votre consultation de 30 minutes est réservée</p>
        </div>

        {/* Content */}
        <div className="p-8">
          {booking && (
            <div className="bg-[#F5F1EB] rounded-xl p-4 mb-6">
              <p className="text-sm text-[#5C5C5C]">Réservation pour</p>
              <p className="font-semibold text-[#2D2D2D]">{booking.customer_name}</p>
              <p className="text-sm text-[#5C5C5C]">{booking.customer_email}</p>
            </div>
          )}

          <div className="text-center mb-6">
            <p className="text-[#5C5C5C] mb-4">
              Dernière étape : choisissez votre créneau de rendez-vous
            </p>
            
            <Button
              onClick={handleCalendlyClick}
              className="w-full bg-gradient-to-r from-[#E2725B] to-[#D4A574] hover:opacity-90 text-white py-6 rounded-full text-lg font-medium"
              data-testid="open-calendly-btn"
            >
              <Calendar className="w-5 h-5 mr-2" />
              Choisir mon créneau
              <ExternalLink className="w-4 h-4 ml-2" />
            </Button>
          </div>

          <div className="border-t border-[#E8E4DE] pt-6">
            <p className="text-sm text-[#8A8A8A] text-center">
              Vous recevrez un email de confirmation après avoir choisi votre créneau sur Calendly.
            </p>
          </div>

          <div className="mt-6 text-center">
            <button
              onClick={() => navigate("/")}
              className="text-[#5C5C5C] hover:text-[#2D2D2D] text-sm underline"
            >
              Retour à l'accueil
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
